import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight, User } from "lucide-react";
import Logo from "@/components/Logo";
import { BOOKING_URL, NAV_LINKS } from "@/constants/site";
import { scrollToSection } from "@/lib/scroll";
import { useAuth } from "@/context/AuthContext";

const go = (e, hash) => {
  e.preventDefault();
  scrollToSection(hash);
};

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 border-b transition-[background-color,border-color] duration-500 ${
          scrolled
            ? "bg-black/60 backdrop-blur-xl border-white/10"
            : "bg-black/20 backdrop-blur-md border-transparent"
        }`}
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 md:px-10">
          <a
            href="#home"
            data-testid="nav-logo"
            onClick={(e) => go(e, "#home")}
            className="transition-opacity duration-300 hover:opacity-80"
          >
            <Logo />
          </a>

          <nav className="hidden items-center gap-8 lg:flex">
            {NAV_LINKS.map((l) => (
              <a
                key={l.hash}
                href={l.hash}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                onClick={(e) => go(e, l.hash)}
                className="group relative font-display text-[13px] font-medium uppercase tracking-[0.22em] text-zinc-300 transition-colors duration-300 hover:text-white"
              >
                {l.label}
                <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-[#E60000] transition-[width] duration-300 group-hover:w-full" />
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            {user ? (
              <a
                href="/account"
                data-testid="nav-account-link"
                className="hidden items-center gap-2 border border-white/15 px-5 py-2.5 font-display text-[13px] font-semibold uppercase tracking-[0.2em] text-white transition-[border-color,transform] duration-300 hover:-translate-y-0.5 hover:border-[#E60000] sm:flex"
              >
                <User size={14} />
                Account
              </a>
            ) : (
              <a
                href="/login"
                data-testid="nav-login-link"
                className="hidden px-4 py-2.5 font-display text-[13px] font-semibold uppercase tracking-[0.2em] text-zinc-300 transition-colors duration-300 hover:text-white sm:flex"
              >
                Login
              </a>
            )}
            <a
              href={BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-book-now-button"
              className="hidden items-center gap-2 bg-[#E60000] px-6 py-2.5 font-display text-[13px] font-semibold uppercase tracking-[0.2em] text-white transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-[#FF1A1A] sm:flex"
            >
              Book Now
              <ArrowUpRight size={15} strokeWidth={2.5} />
            </a>
            <button
              onClick={() => setOpen(true)}
              data-testid="mobile-menu-toggle"
              aria-label="Open menu"
              className="border border-white/15 p-2.5 text-white transition-[border-color,color] duration-300 hover:border-[#E60000] hover:text-[#FF1A1A] lg:hidden"
            >
              <Menu size={20} />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-[80] flex flex-col bg-[#050505]/95 backdrop-blur-2xl lg:hidden"
          >
            <div className="flex items-center justify-between px-5 py-4">
              <Logo />
              <button
                onClick={() => setOpen(false)}
                data-testid="mobile-menu-close"
                aria-label="Close menu"
                className="border border-white/15 p-2.5 text-white transition-[border-color,color] duration-300 hover:border-[#E60000] hover:text-[#FF1A1A]"
              >
                <X size={20} />
              </button>
            </div>
            <nav className="flex flex-1 flex-col justify-center gap-2 px-8">
              {NAV_LINKS.map((l, i) => (
                <motion.a
                  key={l.hash}
                  href={l.hash}
                  data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                  onClick={(e) => {
                    go(e, l.hash);
                    setOpen(false);
                  }}
                  initial={{ opacity: 0, x: -28 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                  className="border-b border-white/10 py-4 font-display text-4xl font-semibold uppercase tracking-wide text-white transition-colors duration-300 hover:text-[#E60000]"
                >
                  {l.label}
                </motion.a>
              ))}
              <motion.a
                href={BOOKING_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-book-now-button"
                onClick={() => setOpen(false)}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.45, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                className="mt-8 flex items-center justify-center gap-2 bg-[#E60000] px-6 py-4 font-display text-lg font-semibold uppercase tracking-[0.2em] text-white transition-[background-color] duration-300 hover:bg-[#FF1A1A]"
              >
                Book Now
                <ArrowUpRight size={20} strokeWidth={2.5} />
              </motion.a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
